import { useCallback, useState } from 'react';
import { connect, type ConnectedProps } from 'react-redux';
import {
  Button,
  IconArrowLeft,
  IconArrowRight,
  InformationalScreen,
  LoadingModal,
  useDirection,
} from '@adc/parallax-component-library';
import { AxiosError } from 'axios';
import {
  FormContainer,
  InputPassword,
  MainContainer,
  PasswordInputValidator,
  WideBanner,
} from 'Components/utility';
import { ApiStatus, ErrorCode } from 'Constants';
import { type FormikHelpers, useFormik } from 'formik';
import { useAccount, useApiError, useAuth, usePasswordValidation } from 'Hooks';
import { updatePassword } from 'Services/patient';
import { type RootState } from 'src/reducers';
import { notifyApp } from 'Utilities/appCommunicator';
import i18n from 'Utilities/i18n';
import mediator from 'Utilities/mediator';

const mapStateToProps = ({
  config: { validation },
  auth: {
    account_id,
    include: { patient },
  },
  env: { osType, osVersion, app, appVersion },
}: RootState) => ({
  validation,
  accountId: account_id,
  osType,
  osVersion,
  app,
  appVersion,
  patient,
});

const initialValuesFormik = {
  currentPassword: '',
  newPassword: '',
  repeatNewPassword: '',
};

type UpdatePasswordFormValues = typeof initialValuesFormik;

const connector = connect(mapStateToProps);

type Props = ConnectedProps<typeof connector>;

const UpdatePassword = ({
  validation,
  accountId,
  osType,
  osVersion,
  app,
  appVersion,
  patient,
}: Props) => {
  const [meetsPasswordRequirements, setMeetsPasswordRequirements] = useState(false);

  const handleClickBack = useCallback(() => {
    mediator.publish('router:back');
    notifyApp('adc-webview:navigate-back-app');
  }, []);

  const isLtr = useDirection() === 'ltr';

  const { showPasswordValidationError } = useAccount('updatePassword');

  const { showApiErrorModal, showNetworkErrorModal } = useApiError();

  const {
    handleLoginError,
    handleLockoutError,
    showHcpError,
    isLocked,
    lockoutMessage,
    lockoutSubmessage,
  } = useAuth('changepassword');

  const validationSchema = usePasswordValidation({
    hasNewPassword: true,
    validateCurrentPassword: true,
    patient,
    translations: {
      currentPassword: {
        required: i18n.t(
          'ChangePassword.content.changePasswordAccountInfoForm.formField.currentPassword.errors.required'
        ),
      },
      newPassword: {
        required: i18n.t(
          'ChangePassword.content.changePasswordAccountInfoForm.formField.newPassword.errors.required'
        ),
        minLength: () => '',
        // PasswordInputValidator handles this error message
        // if needed, uncomment the following lines to return the error message
        // i18n.t(
        //   'ChangePassword.content.changePasswordAccountInfoForm.formField.newPassword.errors.min',
        //   values
        // ),
        maxLength: (values) =>
          i18n.t(
            'ChangePassword.content.changePasswordAccountInfoForm.formField.newPassword.errors.max',
            values
          ),
        trim: i18n.t(
          'ChangePassword.content.changePasswordAccountInfoForm.formField.newPassword.errors.trim'
        ),
        repetitiveSequence: (values) =>
          i18n.t(
            'ChangePassword.content.changePasswordAccountInfoForm.formField.newPassword.errors.repetitiveSequence',
            values
          ),
        context: i18n.t(
          'ChangePassword.content.changePasswordAccountInfoForm.formField.newPassword.errors.context'
        ),
        dictionary: i18n.t(
          'ChangePassword.content.changePasswordAccountInfoForm.formField.newPassword.errors.dictionary'
        ),
        repeatRequired: i18n.t(
          'ChangePassword.content.changePasswordAccountInfoForm.formField.repeatNewPassword.errors.required'
        ),
        mustMatch: i18n.t(
          'ChangePassword.content.changePasswordAccountInfoForm.formField.repeatNewPassword.errors.passwordMustMatch'
        ),
        differentFromCurrent: i18n.t(
          'ChangePassword.content.changePasswordAccountInfoForm.formField.newPassword.errors.differentFromCurrent'
        ),
      },
    },
  });

  const handleClickForgotPass = () => {
    mediator.publish('router:navigate', '/forgot-password');
  };

  const handleApiRequestError = useCallback(
    (
      error: AxiosError<ApiErrorData>,
      resetForm: () => void,
      setFieldValue: (name: string, value: unknown) => void,
      setTouched: (fields: Record<string, boolean>) => void
    ) => {
      switch (error.status) {
        case ApiStatus.FORBIDDEN: {
          const code = error.response?.data.code;

          if (code === ApiStatus.FORBIDDEN_HCP_ACCOUNT) {
            showHcpError();
          }

          break;
        }

        case ApiStatus.UNAUTHORIZED:
          handleLoginError(error, resetForm, () => {
            setFieldValue('currentPassword', '');
          });
          break;

        case ApiStatus.BAD_REQUEST: {
          const errors = error.response?.data.errors;

          setFieldValue('newPassword', '');
          setFieldValue('repeatNewPassword', '');

          setTouched({});

          showPasswordValidationError({ validation }, errors);
          break;
        }

        case ApiStatus.TOO_MANY_REQUESTS: {
          handleLockoutError(error);
          resetForm();
          break;
        }

        default: {
          if (error.code === ErrorCode.NETWORK_ERROR) {
            showNetworkErrorModal();
          } else {
            showApiErrorModal();
          }
        }
      }
    },
    [
      handleLockoutError,
      handleLoginError,
      showApiErrorModal,
      showHcpError,
      showNetworkErrorModal,
      showPasswordValidationError,
      validation,
    ]
  );

  const onSubmitForm = useCallback(
    async (
      values: UpdatePasswordFormValues,
      {
        setSubmitting,
        resetForm,
        setFieldValue,
        setTouched,
      }: FormikHelpers<UpdatePasswordFormValues>
    ) => {
      try {
        await updatePassword(
          {
            userID: accountId,
            emailConfirmation: patient.email,
            newPassword: values.newPassword,
            currentPassword: values.currentPassword,
          },
          osType,
          osVersion,
          app,
          appVersion
        );
        notifyApp('adc-webview:update-password');
      } catch (error) {
        handleApiRequestError(
          error as AxiosError<ApiErrorData>,
          resetForm,
          setFieldValue,
          setTouched
        );
      }

      setSubmitting(false);
    },
    [accountId, app, appVersion, patient.email, osType, osVersion, handleApiRequestError]
  );

  const { handleSubmit, handleBlur, handleChange, isValid, isSubmitting, errors, touched, values } =
    useFormik({
      initialValues: initialValuesFormik,
      onSubmit: onSubmitForm,
      validationSchema,
      validateOnBlur: true,
    });

  return (
    <MainContainer>
      {isSubmitting && <LoadingModal aria-label="Loading" />}

      <InformationalScreen>
        <InformationalScreen.Page
          topNavigationProps={{
            title: i18n.t('ChangePassword.title'),
            buttonStart: {
              element: isLtr ? (
                <IconArrowLeft testID="ChangePassword.back-button" />
              ) : (
                <IconArrowRight testID="ChangePassword.back-button" />
              ),
              label: i18n.t('ChangePassword.title'),
              onPress: handleClickBack,
            },
            testID: 'ChangePassword.title',
          }}
        >
          <InformationalScreen.Content>
            <FormContainer hasHiddenUserInput>
              {lockoutMessage ? (
                <WideBanner
                  testID="ChangePassword"
                  message={lockoutMessage}
                  submessage={lockoutSubmessage}
                />
              ) : null}
              <InputPassword
                label={i18n.t(
                  'ChangePassword.content.changePasswordAccountInfoForm.formField.currentPassword.label'
                )}
                placeholder={i18n.t(
                  'ChangePassword.content.changePasswordAccountInfoForm.formField.currentPassword.placeholder'
                )}
                isInvalid={Boolean(errors.currentPassword && touched.currentPassword)}
                errorMessage={errors.currentPassword}
                testID="currentPassword"
                onChangeText={handleChange('currentPassword')}
                onBlur={handleBlur('currentPassword')}
                defaultValue={values.currentPassword}
                disabled={isLocked}
              />
              <Button
                variant="tertiary"
                label={i18n.t(
                  'ChangePassword.content.changePasswordAccountInfoForm.links.forgotPasswordLink.label'
                )}
                onPress={handleClickForgotPass}
                testID="ChangePassword.forgotPassword"
              />
              <PasswordInputValidator
                placeholder={i18n.t(
                  'ChangePassword.content.changePasswordAccountInfoForm.formField.newPassword.placeholder'
                )}
                label={i18n.t(
                  'ChangePassword.content.changePasswordAccountInfoForm.formField.newPassword.label'
                )}
                errors={errors}
                touched={touched}
                handleBlur={handleBlur('newPassword')}
                password={values.newPassword}
                onValidate={setMeetsPasswordRequirements}
                identities={validation?.identity}
                onChangeText={handleChange('newPassword')}
                testID="newPassword"
                disabled={isLocked}
              />
              <InputPassword
                mt="$1"
                label={i18n.t(
                  'ChangePassword.content.changePasswordAccountInfoForm.formField.repeatNewPassword.label'
                )}
                placeholder={i18n.t(
                  'ChangePassword.content.changePasswordAccountInfoForm.formField.repeatNewPassword.placeholder'
                )}
                isInvalid={Boolean(errors.repeatNewPassword && touched.repeatNewPassword)}
                errorMessage={errors.repeatNewPassword}
                testID="repeatNewPassword"
                onChangeText={handleChange('repeatNewPassword')}
                onBlur={handleBlur('repeatNewPassword')}
                defaultValue={values.repeatNewPassword}
                disabled={isLocked}
              />
            </FormContainer>
          </InformationalScreen.Content>
          <InformationalScreen.Footer>
            <Button
              label={i18n.t('Global.microcopy.common.save')}
              disabled={
                !values.currentPassword ||
                !values.newPassword ||
                !values.repeatNewPassword ||
                !isValid ||
                isSubmitting ||
                !meetsPasswordRequirements
              }
              onPress={() => {
                handleSubmit();
              }}
              testID="ChangePassword.save-button"
            />
          </InformationalScreen.Footer>
        </InformationalScreen.Page>
      </InformationalScreen>
    </MainContainer>
  );
};

export default connector(UpdatePassword);
